import { Button, Input, Form, Upload, message, ConfigProvider } from '@fuxi/eevee-ui';
import type { UploadProps } from '@fuxi/eevee-ui';
import { UploadFile } from 'antd/lib';
import { PlusOutlined } from '@ant-design/icons';
import { CustomEvent } from 'common/tracker/custom-event';
import { sendEvent } from 'common/tracker';
import { useBatchUpload } from 'common/hooks/useBatchUpload';
import { useTaskDetailContext } from '@/context/TaskDetailContext';
import AttachmentList from '@/components/AttachmentList';

import { UploadFileType } from '@/constants';
import cx from './index.module.css';

const { TextArea } = Input;

interface AddCommentProps {
  isShow: boolean;
  commentForm: any;
  content: string;
  attachment: Array<UploadFile>;
  setAttachment: (attachment: UploadFile[]) => void;
  setIsUploading: (isUploading: boolean) => void;
  onInputFocus?: () => void;
  width: number;
  maxSize: number; //上传文件总大小限制
  maxCount: number; // 上传文件个数限制
}
const AddComment: React.FC<AddCommentProps> = ({
  isShow,
  commentForm,
  content,
  attachment,
  setAttachment,
  setIsUploading,
  onInputFocus = () => {},
  width,
  maxSize,
  maxCount,
}) => {
  const { projectCode, roleInfo, requirementType, stage } = useTaskDetailContext();
  const { batchUpload } = useBatchUpload();

  const onRemoveFile = ({ name }) => {
    const index = attachment.findIndex(file => file?.name === name);
    if (index > -1) {
      // 删掉attachment里的文件
      const newAttachment = [...attachment];
      newAttachment.splice(index, 1);

      setAttachment(newAttachment);
      return true;
    }
    return false;
  };
  const customRequest = ({ file, onProgress, onSuccess, onError }: any) => {
    batchUpload({
      files: [file],
      fileType: UploadFileType.reviewAttachment,
      onFileSuc: (_, __, url) => {
        const att = [...attachment];
        const index = att.findIndex(item => item.name === file.name);
        if (index !== -1) {
          att[index].percent = 100;
          att[index].url = url;
        }
        setAttachment([...att]);
        onSuccess({ result: 0, file: att[index] });
      },
      onError: () => {
        onError(new Error('上传失败'));
      },
      onTotalProgress: (_, x, percent) => {
        onProgress({ percent: Number(percent) * 100 });
      },
      onSuccess: (_, __, { duration, totalSize }) => {
        sendEvent(CustomEvent.UploadAttachment, {
          et: 'OTHER',
          c1: projectCode,
          c2: roleInfo?.originRole,
          c3: requirementType,
          c4: stage,
          c5: totalSize,
          c6: duration,
          c7: UploadFileType.reviewAttachment,
        });
      },
    });
  };
  const beforeUpload = (file, fileList) => {
    //判断fileList里的文件总大小有没有超出maxSize,超出则不上传
    const totalSize = fileList.reduce((acc, cur) => acc + cur.size, 0);
    if (totalSize > maxSize) {
      message.error('上传文件总大小不能超过' + maxSize / 1024 / 1024 + 'M');
      return Upload.LIST_IGNORE;
    }
    if (fileList.length > maxCount) {
      message.error('上传文件个数不能超过' + maxCount + '个');
      return Upload.LIST_IGNORE;
    }
    //判断是否有同名文件
    const isExist = attachment.some(item => item.name === file.name);
    if (isExist) {
      message.error('已存在同名文件');
      return Upload.LIST_IGNORE;
    }
    return true;
  };
  const uploadProps: UploadProps = {
    maxCount,
    customRequest,
    beforeUpload,
    fileList: attachment,
    showUploadList: false,
    onChange: ({ file, fileList, event }) => {
      if (file.status === 'error') {
        message.error('上传失败');
        onRemoveFile({ name: file.name });
      } else {
        let newFileList = [...fileList];
        newFileList = newFileList.map(file => {
          if (file.response) {
            file.url = file.response.file.url;
          }
          return file;
        });
        setAttachment([...newFileList]);
      }

      //是否有正在上传的文件,返回布尔值
      const isUploading = fileList.some(item => item.status === 'uploading');
      setIsUploading(isUploading);
    },
  };
  return (
    <ConfigProvider theme={{ components: { Tooltip: { zIndexPopup: 10000 } } }}>
      <div className={cx('info-content-wrap', isShow && 'info-content-wrap-show')}>
        <div className={cx('comment-form-wrap')}>
          <Form layout="vertical" form={commentForm} initialValues={{ content }} autoComplete="off">
            <Form.Item name="content">
              <TextArea
                showCount
                rows={4}
                placeholder="请输入反馈内容..."
                maxLength={400}
                onFocus={onInputFocus}
                className={cx('comment-textarea')}
              />
            </Form.Item>
          </Form>

          <div className={cx('tip')}>
            <span className={cx('label')}>附件</span>
            <Upload {...uploadProps}>
              <Button
                icon={<PlusOutlined />}
                type="default"
                disabled={
                  attachment &&
                  (attachment.length >= maxCount || attachment.reduce((acc, cur) => acc + (cur.size || 0), 0) > maxSize)
                }></Button>
              <span className={cx('item-description')} onClick={e => e.stopPropagation()}>
                最多支持上传{maxCount}个附件，单个文件不超过100M
              </span>
            </Upload>
          </div>

          <AttachmentList attachment={attachment} onRemove={onRemoveFile} />
        </div>
      </div>
    </ConfigProvider>
  );
};
export default AddComment;
